import React, { useState, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Sidebar from "../Sidebar";
import Header from "../Header";
import "./Integration.css";
import {
  BsLink45Deg,
  BsXLg,
  BsArrowRight,
  BsCheckCircleFill,
  BsLightningChargeFill,
  BsGearFill,
  BsPlugFill,
  BsPhoneFill,
  BsTrashFill,
  BsWhatsapp,
  BsApple,
} from "react-icons/bs";
import logoGhl from "../../assets/integration-ghl.png";
import logoZapier from "../../assets/integration-zapier.png";
import logoMake from "../../assets/integration-make.png";
import logoPabbly from "../../assets/integration-pabbly.png";

import API from "../../config";

async function getGhlConnectUrl() {
  try {
    const token = localStorage.getItem("userToken");
    const { data } = await import("axios").then(m => m.default.get(`${API}/ghl/oauth/url`, {
      headers: { Authorization: `Bearer ${token}` },
    }));
    return data.url;
  } catch (e) {
    console.error("Failed to get GHL OAuth URL:", e);
    return null;
  }
}

const integrations = [
  {
    key: "ghl",
    name: "GoHighLevel",
    logo: logoGhl,
    color: "#4f46e5",
    description: "Connect your CRM to send and receive SMS through Pairfon devices.",
    category: "CRM",
    getConnectUrl: getGhlConnectUrl,
  },
  {
    key: "zapier",
    name: "Zapier",
    logo: logoZapier,
    color: "#ff4a00",
    description: "Automate workflows by connecting Pairfon with 5,000+ apps.",
    category: "Automation",
    connectUrl: "https://zapier.com/developer/public-invite/237911/f6cbc8bfc6541c57c0ca094d303d5f25/",
  },
  {
    key: "make",
    name: "Make.com",
    logo: logoMake,
    color: "#6d3bea",
    description: "Build advanced automations with visual workflows and Pairfon triggers.",
    category: "Automation",
    connectUrl: "https://eu1.make.com/app/invite/1e6a9eb6e0a003dba0ea0d58d5d670da",
  },
  {
    key: "pabbly",
    name: "Pabbly",
    logo: logoPabbly,
    color: "#2563eb",
    description: "Connect Pairfon with Pabbly Connect to automate SMS workflows across apps.",
    category: "Automation",
    connectUrl:
      "https://connect.pabbly.com/share-app/WhJZa1QDB2QASlM5UxwEI1tPAAQGX1EwVE0CEFZZVy8GS1IpBURbMV0RAmhWBVMyXxYJYw9TUGgJHQIGUwUHdF1HAhcBHgJfADMCRwRbWHJaOFkuVB8HPQBRU2BTGwQ-W1QAJwZLUQxUWgI5Vk1XEQZfUikFS1t7XR0CalYZU3lfGgk4D0RQagkKAmdTGQdpXVYCZwETAl8AMwJHBFtYclo4",
  },
];

const getToken = () => localStorage.getItem("userToken");

export default function Integration() {
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [ghlBanner, setGhlBanner] = useState(null);
  const [ghlError, setGhlError] = useState(null);
  const [ghlConnected, setGhlConnected] = useState(false);
  const [ghlInstallations, setGhlInstallations] = useState([]);
  const [devices, setDevices] = useState([]);
  const [deviceSelections, setDeviceSelections] = useState({});
  const [simSelections, setSimSelections] = useState({});
  const [waNumbers, setWaNumbers] = useState([]);
  const [imHandles, setImHandles] = useState([]);
  const [waSelections, setWaSelections] = useState({});
  const [imSelections, setImSelections] = useState({});
  const [savingLocation, setSavingLocation] = useState(null);
  const [deletingLocation, setDeletingLocation] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [ghlAssignedDevices, setGhlAssignedDevices] = useState([]);
  const [savingAssigned, setSavingAssigned] = useState(false);
  const [ghlUrlLoading, setGhlUrlLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const handleGhlConnect = async () => {
    setGhlUrlLoading(true);
    try {
      const url = await getGhlConnectUrl();
      if (url) window.open(url, "_blank", "noopener noreferrer");
    } finally {
      setGhlUrlLoading(false);
    }
  };

  const fetchGhlStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/ghl/status`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setGhlConnected(data.connected);
      setGhlInstallations(data.installations || []);
      const devSel = {};
      const simSel = {};
      const waSel = {};
      const imSel = {};
      for (const inst of data.installations || []) {
        devSel[inst.location_id] = inst.default_device_fingerprint || "";
        simSel[inst.location_id] = inst.default_sim_slot || "SIM1";
        waSel[inst.location_id] = inst.default_wa_number_id || "";
        imSel[inst.location_id] = inst.default_im_handle_id || "";
      }
      setDeviceSelections(devSel);
      setSimSelections(simSel);
      setWaSelections(waSel);
      setImSelections(imSel);
    } catch (err) {
      console.error("Failed to fetch GHL status:", err);
    }
  }, []);

  const fetchWaNumbers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/whatsapp/numbers`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setWaNumbers(data.numbers || data || []);
    } catch (err) { /* channel may not be active */ }
  }, []);

  const fetchImHandles = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/imessage/handles`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setImHandles(data.handles || data.agents || data || []);
    } catch (err) { /* channel may not be active */ }
  }, []);

  const fetchDevices = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/dashboard/devices`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setDevices(data.devices || data || []);
    } catch (err) {
      console.error("Failed to fetch devices:", err);
    }
  }, []);

  const fetchGhlAssignedDevices = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/ghl/assigned-devices`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setGhlAssignedDevices(data.devices || []);
    } catch (err) {
      console.error("Failed to fetch GHL assigned devices:", err);
    }
  }, []);

  useEffect(() => {
    fetchGhlStatus();
    fetchDevices();
    fetchGhlAssignedDevices();
    fetchWaNumbers();
    fetchImHandles();
  }, [fetchGhlStatus, fetchDevices, fetchGhlAssignedDevices, fetchWaNumbers, fetchImHandles]);

  useEffect(() => {
    const ghl = searchParams.get("ghl");
    if (ghl === "connected") {
      setGhlBanner("connected");
      fetchGhlStatus();
      // Auto-link company
      const locationId = searchParams.get("location");
      if (locationId) {
        axios.post(`${API}/ghl/link-company`, { locationId }, {
          headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json" },
        })
          .then((r) => {
            // Direct customers configure senders on the manage page; partner
            // customers are pre-configured automatically, so no redirect.
            if (!r.data?.isPartner) navigate("/integration/ghl");
          })
          .catch(() => {});
      }
      searchParams.delete("ghl");
      searchParams.delete("location");
      setSearchParams(searchParams, { replace: true });
    } else if (ghl === "error") {
      setGhlBanner("error");
      setGhlError(searchParams.get("ghl_error") || null);
      searchParams.delete("ghl");
      searchParams.delete("ghl_error");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, fetchGhlStatus]);

  const saveDefaultDevice = async (locationId) => {
    setSavingLocation(locationId);
    try {
      await axios.put(
        `${API}/ghl/channel-defaults`,
        {
          location_id: locationId,
          device_fingerprint: deviceSelections[locationId] || null,
          sim_slot: simSelections[locationId] || "SIM1",
          wa_number_id: waSelections[locationId] || null,
          im_handle_id: imSelections[locationId] || null,
        },
        { headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json" } }
      );
      setGhlBanner("device-saved");
      setTimeout(() => setGhlBanner(null), 3000);
    } catch (err) {
      console.error("Failed to save channel defaults:", err);
    }
    setSavingLocation(null);
  };

  const deleteLocation = async (locationId) => {
    setDeletingLocation(locationId);
    try {
      await axios.delete(`${API}/ghl/installations/${locationId}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setConfirmDelete(null);
      await fetchGhlStatus();
      setGhlBanner("location-deleted");
      setTimeout(() => setGhlBanner(null), 3000);
    } catch (err) {
      console.error("Failed to delete location:", err);
      setConfirmDelete(null);
    }
    setDeletingLocation(null);
  };

  const toggleGhlDevice = (fingerprint) => {
    setGhlAssignedDevices((prev) =>
      prev.includes(fingerprint) ? prev.filter((f) => f !== fingerprint) : [...prev, fingerprint]
    );
  };

  const saveGhlAssignedDevices = async () => {
    setSavingAssigned(true);
    try {
      await axios.put(
        `${API}/ghl/assigned-devices`,
        { device_fingerprints: ghlAssignedDevices },
        { headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json" } }
      );
      setGhlBanner("devices-assigned");
      setTimeout(() => setGhlBanner(null), 3000);
    } catch (err) {
      console.error("Failed to save assigned devices:", err);
    }
    setSavingAssigned(false);
  };

  const ghlDeviceList = ghlAssignedDevices.length > 0
    ? devices.filter((d) => ghlAssignedDevices.includes(d.device_id))
    : devices;

  const getDeviceSims = (device) => {
    const sim = (device.selected_sim || "SIM1").toUpperCase();
    if (sim === "BOTH") return ["SIM1", "SIM2"];
    return [sim];
  };

  return (
    <div className="page-container">
      <Sidebar />
      <div className="main-content">
        <Header />
        <div className="container mt-4">
          <div className="page-header">
            <h4>Integrations</h4>
            <p className="page-subtitle">Connect Pairfon with your favorite tools</p>
          </div>

          {ghlBanner === "connected" && (
            <div className="integration-banner integration-banner-success">
              <BsCheckCircleFill /> GoHighLevel connected successfully!
              <button className="banner-dismiss" onClick={() => setGhlBanner(null)}><BsXLg /></button>
            </div>
          )}
          {ghlBanner === "device-saved" && (
            <div className="integration-banner integration-banner-success">
              <BsCheckCircleFill /> Default device updated.
              <button className="banner-dismiss" onClick={() => setGhlBanner(null)}><BsXLg /></button>
            </div>
          )}
          {ghlBanner === "devices-assigned" && (
            <div className="integration-banner integration-banner-success">
              <BsCheckCircleFill /> GHL devices updated.
              <button className="banner-dismiss" onClick={() => setGhlBanner(null)}><BsXLg /></button>
            </div>
          )}
          {ghlBanner === "location-deleted" && (
            <div className="integration-banner integration-banner-success">
              <BsCheckCircleFill /> Sub-account removed.
              <button className="banner-dismiss" onClick={() => setGhlBanner(null)}><BsXLg /></button>
            </div>
          )}
          {ghlBanner === "error" && (
            <div className="integration-banner integration-banner-error">
              <div>
                GoHighLevel connection failed. Please try again.
                {ghlError && <div style={{ fontSize: "0.8rem", fontWeight: 400, marginTop: 4, opacity: 0.85 }}>{ghlError}</div>}
              </div>
              <button className="banner-dismiss" onClick={() => setGhlBanner(null)}><BsXLg /></button>
            </div>
          )}

          <div className="integration-grid">
            {integrations.map((integration) => (
              <div key={integration.key} className={`integration-card ${integration.key === "ghl" && ghlConnected ? "integration-card-connected" : ""}`}>
                <div className="integration-card-header">
                  <div className="integration-logo-wrap">
                    <img src={integration.logo} alt={integration.name} className="integration-logo-img" />
                  </div>
                  {integration.key === "ghl" && ghlConnected ? (
                    <span className="integration-status-badge connected"><BsCheckCircleFill /> Connected</span>
                  ) : (
                    <span className="integration-category">{integration.category}</span>
                  )}
                </div>
                <h6 className="integration-name">{integration.name}</h6>
                <p className="integration-desc">{integration.description}</p>

                {integration.key === "ghl" && ghlConnected ? (
                  <button
                    className="integration-connect-btn integration-manage-btn"
                    onClick={() => navigate("/integration/ghl")}
                  >
                    <BsGearFill /> Manage <BsArrowRight />
                  </button>
                ) : (
                  <button
                    className="integration-connect-btn"
                    onClick={() => setSelectedIntegration(integration)}
                  >
                    <BsPlugFill /> Connect <BsArrowRight />
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* How it works */}
          <div className="integration-how-section">
            <h5>How Integrations Work</h5>
            <div className="integration-steps">
              <div className="integration-step">
                <div className="step-icon step-icon-1"><BsLink45Deg /></div>
                <h6>Connect</h6>
                <p>Link your account with the integration platform.</p>
              </div>
              <div className="integration-step">
                <div className="step-icon step-icon-2"><BsGearFill /></div>
                <h6>Configure</h6>
                <p>Set up triggers and actions for your SMS workflows.</p>
              </div>
              <div className="integration-step">
                <div className="step-icon step-icon-3"><BsLightningChargeFill /></div>
                <h6>Automate</h6>
                <p>Messages flow automatically through your Pairfon devices.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Connect / Manage Modal */}
      {selectedIntegration && (
        <div className="modal-overlay" onClick={() => { if (!confirmDelete) setSelectedIntegration(null); }}>
          <div className={`modal-card modal-form ${selectedIntegration.key === "ghl" && ghlConnected ? "modal-manage" : ""}`} onClick={(e) => e.stopPropagation()}>
            <div className="modal-form-header">
              <h5>{selectedIntegration.key === "ghl" && ghlConnected ? "Manage" : "Connect"} {selectedIntegration.name}</h5>
              <button className="modal-close" onClick={() => setSelectedIntegration(null)}><BsXLg /></button>
            </div>
            <div className="modal-form-body" style={{ textAlign: "center", padding: "32px 24px" }}>
              <div className="integration-modal-logo">
                <img src={selectedIntegration.logo} alt={selectedIntegration.name} />
              </div>

              {selectedIntegration.key === "ghl" && ghlConnected ? (
                <div style={{ textAlign: "left" }}>
                  <div className="integration-status-connected-large" style={{ justifyContent: "flex-start", marginBottom: 16 }}>
                    <BsCheckCircleFill /> {ghlInstallations.length} Sub-account{ghlInstallations.length !== 1 ? "s" : ""} Connected
                  </div>

                  {/* Assigned Devices Section */}
                  <div className="ghl-assigned-section">
                    <div className="ghl-assigned-header">
                      <h6><BsPhoneFill style={{ marginRight: 6 }} /> GHL Devices</h6>
                      <p>Select which devices are available for GHL workflows and conversations.</p>
                    </div>
                    <div className="ghl-assigned-list">
                      {devices.map((d) => (
                        <label key={d.device_id} className={`ghl-assigned-item ${ghlAssignedDevices.includes(d.device_id) ? "checked" : ""}`}>
                          <input
                            type="checkbox"
                            checked={ghlAssignedDevices.includes(d.device_id)}
                            onChange={() => toggleGhlDevice(d.device_id)}
                          />
                          <span className="ghl-assigned-name">{d.device_name || d.device_id}</span>
                          <span className={`ghl-assigned-status ${d.status === "active" ? "online" : ""}`}>
                            {d.status === "active" ? "Online" : "Offline"}
                          </span>
                        </label>
                      ))}
                    </div>
                    <button
                      className="btn-save ghl-save-btn"
                      style={{ width: "100%", marginTop: 10 }}
                      onClick={saveGhlAssignedDevices}
                      disabled={savingAssigned}
                    >
                      {savingAssigned ? "Saving..." : "Save Device Selection"}
                    </button>
                  </div>

                  {/* Sub-accounts */}
                  <div className="ghl-settings-header" style={{ marginTop: 24 }}>
                    <h6>Sub-Account Senders</h6>
                    <p>Choose the default sender per channel for each connected sub-account. Blank = auto-pick.</p>
                  </div>

                  <div className="ghl-table-wrap">
                    <table className="ghl-table">
                      <thead>
                        <tr>
                          <th>Sub-account</th>
                          <th><BsPhoneFill className="ghl-th-sms" /> SMS</th>
                          <th><BsWhatsapp className="ghl-th-wa" /> WhatsApp</th>
                          <th><BsApple className="ghl-th-im" /> iMessage</th>
                          <th aria-label="actions"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {ghlInstallations.map((inst) => (
                          <tr key={inst.location_id}>
                            <td className="ghl-td-name">
                              <span className="ghl-account-name">{inst.location_name || inst.location_id}</span>
                              <span className="ghl-account-sub">{new Date(inst.installed_at).toLocaleDateString()}</span>
                            </td>
                            <td>
                              <div className="ghl-cell-fields">
                                <select
                                  className="ghl-select"
                                  value={deviceSelections[inst.location_id] || ""}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    setDeviceSelections((prev) => ({ ...prev, [inst.location_id]: val }));
                                    const dev = devices.find((d) => d.device_id === val);
                                    if (dev) {
                                      const sims = getDeviceSims(dev);
                                      if (!sims.includes(simSelections[inst.location_id])) {
                                        setSimSelections((prev) => ({ ...prev, [inst.location_id]: sims[0] }));
                                      }
                                    }
                                  }}
                                >
                                  <option value="">{ghlDeviceList.length ? "Auto" : "None"}</option>
                                  {ghlDeviceList.map((d) => (
                                    <option key={d.device_id} value={d.device_id}>
                                      {d.device_name || d.device_id}{d.status === "active" ? " •" : ""}
                                    </option>
                                  ))}
                                </select>
                                <select
                                  className="ghl-select ghl-select-sim"
                                  value={simSelections[inst.location_id] || "SIM1"}
                                  onChange={(e) => setSimSelections((prev) => ({ ...prev, [inst.location_id]: e.target.value }))}
                                >
                                  {deviceSelections[inst.location_id] && devices.find((d) => d.device_id === deviceSelections[inst.location_id]) ? (
                                    getDeviceSims(devices.find((d) => d.device_id === deviceSelections[inst.location_id])).map((s) => (
                                      <option key={s} value={s}>{s}</option>
                                    ))
                                  ) : (
                                    <>
                                      <option value="SIM1">SIM1</option>
                                      <option value="SIM2">SIM2</option>
                                    </>
                                  )}
                                </select>
                              </div>
                            </td>
                            <td>
                              <select
                                className="ghl-select"
                                value={waSelections[inst.location_id] || ""}
                                onChange={(e) => setWaSelections((prev) => ({ ...prev, [inst.location_id]: e.target.value }))}
                              >
                                <option value="">{waNumbers.length ? "Auto" : "None"}</option>
                                {waNumbers.map((n) => (
                                  <option key={n.id} value={n.id}>{n.phone || n.number || n.id}{n.status === "connected" ? " •" : ""}</option>
                                ))}
                              </select>
                            </td>
                            <td>
                              <select
                                className="ghl-select"
                                value={imSelections[inst.location_id] || ""}
                                onChange={(e) => setImSelections((prev) => ({ ...prev, [inst.location_id]: e.target.value }))}
                              >
                                <option value="">{imHandles.length ? "Auto" : "None"}</option>
                                {imHandles.map((h) => (
                                  <option key={h.id} value={h.id}>{h.handle || h.apple_id || h.id}{h.status === "online" ? " •" : ""}</option>
                                ))}
                              </select>
                            </td>
                            <td className="ghl-td-actions">
                              <button
                                className="btn-save ghl-save-sm"
                                onClick={() => saveDefaultDevice(inst.location_id)}
                                disabled={savingLocation === inst.location_id}
                              >
                                {savingLocation === inst.location_id ? "…" : "Save"}
                              </button>
                              <button
                                className="ghl-delete-btn"
                                title="Remove sub-account"
                                onClick={() => setConfirmDelete(inst.location_id)}
                              >
                                <BsTrashFill />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 20 }}>
                    <button
                      className="btn-save"
                      style={{ width: "100%" }}
                      onClick={handleGhlConnect}
                      disabled={ghlUrlLoading}
                    >
                      <BsPlugFill style={{ marginRight: 6 }} /> {ghlUrlLoading ? "Opening..." : "Connect Another Sub-Account"} <BsArrowRight />
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <p style={{ color: "var(--pf-text-muted)", marginBottom: 24 }}>
                    {selectedIntegration.key === "ghl"
                      ? <>Authorize Pairfon to connect with your GoHighLevel location. SMS will appear as a custom channel in <strong>GHL Conversations</strong>. You can also use the <strong>Pairfon trigger & action</strong> in GHL workflows.</>
                      : (selectedIntegration.connectUrl || selectedIntegration.getConnectUrl)
                      ? <>Accept the invite to add Pairfon to your {selectedIntegration.name} account. You'll need your <strong>API key</strong> to connect — find it on the <strong>API & Webhooks</strong> page.</>
                      : <>Use your Pairfon API key to connect with {selectedIntegration.name}. Go to <strong>API & Webhooks</strong> to get your key, then paste it in your {selectedIntegration.name} integration settings.</>
                    }
                  </p>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {selectedIntegration.key === "ghl" ? (
                      <button
                        className="btn-save"
                        style={{ width: "100%" }}
                        onClick={handleGhlConnect}
                        disabled={ghlUrlLoading}
                      >
                        {ghlUrlLoading ? "Opening..." : `Connect with ${selectedIntegration.name}`} <BsArrowRight />
                      </button>
                    ) : (
                      <a
                        href={selectedIntegration.connectUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-save"
                        style={{ width: "100%", textAlign: "center", textDecoration: "none" }}
                      >
                        Connect with {selectedIntegration.name} <BsArrowRight />
                      </a>
                    )}
                    {selectedIntegration.key !== "ghl" && (
                      <button
                        className="btn-cancel"
                        style={{ width: "100%" }}
                        onClick={() => {
                          setSelectedIntegration(null);
                          window.location.href = "/api";
                        }}
                      >
                        Get API Key
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Popup — rendered via portal to guarantee it's on top */}
      {confirmDelete && ReactDOM.createPortal(
        <div className="modal-overlay confirm-overlay" onClick={() => setConfirmDelete(null)}>
          <div className="confirm-popup" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-popup-icon">
              <BsTrashFill />
            </div>
            <h6>Remove Sub-Account</h6>
            <p>
              Are you sure you want to remove <strong>{confirmDelete}</strong>? SMS will stop flowing for this location.
            </p>
            <div className="confirm-popup-actions">
              <button className="btn-cancel" onClick={() => setConfirmDelete(null)} disabled={!!deletingLocation}>
                Cancel
              </button>
              <button
                className="btn-danger"
                onClick={() => deleteLocation(confirmDelete)}
                disabled={!!deletingLocation}
              >
                {deletingLocation ? "Removing..." : "Remove"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
